import { useState, useEffect, useRef } from "react";
import { useApp, api, timeAgo } from "../context";
import { StickerSheet } from "../StickerPicker";
import { Shell, Btn, EmojiPicker, EmptyState } from "../components";
import PostCard from "../PostCard";
import StoriesBar from "../StoriesBar";

const FireIcon = ({ size = 14 }) => (
  <>
    <style>{`@keyframes flicker{0%{transform:scale(1) rotate(-2deg)}50%{transform:scale(1.15) rotate(2deg)}100%{transform:scale(1) rotate(-2deg)}}`}</style>
    <span style={{ display:"inline-block", animation:"flicker 0.8s ease-in-out infinite", fontSize:size, lineHeight:1 }}>🔥</span>
  </>
);

const TABS = [
  { id:"for_you", label:"For You" },
  { id:"following", label:"Following" },
  { id:"trending", label:"Trending", fire:true },
  { id:"meetups", label:"Meetups", badge:true },
  { id:"streaming", label:"Streaming" },
  { id:"events", label:"Events" },
];

const FeedPage = ({ DISPLAY_TOPICS, topic, setTopic, showComposer, setShowComposer,
  newPostTopic, setNewPostTopic, showPollCreator, setShowPollCreator, pollQuestion, setPollQuestion,
  pollOptions, setPollOptions, showEmoji, setShowEmoji, emojiTarget, setEmojiTarget,
  doPost, loading, feedLoading, filteredPosts, showInstall, handleInstall, dismissInstall,
  showOnboard, setShowOnboard, onboardStep, setOnboardStep, textRef, emojiRef }) => {

  const { c, go, user, dark, flash, setViewingUser } = useApp();
  const [newPost, setNewPost] = useState("");
const newPostRef = useRef("");
  const [activeTab, setActiveTab] = useState("for_you");
  const [postSticker, setPostSticker] = useState(null);
  const [showPostStickerPicker, setShowPostStickerPicker] = useState(false);
  const [comStats, setComStats] = useState({ online_count:0, online_users:[], total_members:0, suggested:[] });
  const [followingPosts, setFollowingPosts] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [meetupPreviews, setMeetupPreviews] = useState([]);
  const [moviePreviews, setMoviePreviews] = useState([]);
  const [eventPreviews, setEventPreviews] = useState([]);
  const [meetupBadge, setMeetupBadge] = useState(0);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [mediaType, setMediaType] = useState(null);
  

  const resetComposer = () => {
    setShowComposer(false);
    setNewPost("");
    setShowPollCreator(false);
    setPollQuestion("");
    setPollOptions(["",""]);
    setPostSticker(null);
    setShowPostStickerPicker(false);
    setMediaFiles([]);
    setMediaPreviews([]);
    setMediaType(null);
    setShowEmoji(false);
  };

  const compressImage = (file) => new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const img = new Image();
    img.onload = () => {
      const MAX = 1280;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => resolve(new File([blob], file.name, { type: "image/jpeg" })), "image/jpeg", 0.82);
    };
    img.src = URL.createObjectURL(file);
  });

  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const isVideo = files[0].type.startsWith("video/");
    const isImage = files[0].type.startsWith("image/");
    if (!isVideo && !isImage) { flash("Only images or videos allowed", "error"); return; }
    if (isVideo && files[0].size > 100 * 1024 * 1024) { flash("Video max 100MB", "error"); return; }
    setMediaType(isVideo ? "video" : "image");
    if (isImage) {
      const compressed = await Promise.all(files.map(f => compressImage(f)));
      setMediaFiles(compressed);
      const previews = await Promise.all(compressed.map(f => new Promise(res => {
        const reader = new FileReader();
        reader.onload = ev => res({ url: ev.target.result, type: f.type });
        reader.readAsDataURL(f);
      })));
      setMediaPreviews(previews);
    } else {
      setMediaFiles(files);
      const reader = new FileReader();
      reader.onload = (ev) => setMediaPreviews([{ url: ev.target.result, type: files[0].type }]);
      reader.readAsDataURL(files[0]);
    }
  };

  useEffect(() => {
    const fetchStats = () => {
      api("/community/stats").then(r => {
        if (r.ok) {
          setComStats(prev => {
            const same = prev.online_count === r.data.online_count &&
              JSON.stringify(prev.online_users?.map(u=>u.id)) === JSON.stringify(r.data.online_users?.map(u=>u.id)) &&
              prev.suggested?.length === r.data.suggested?.length;
            return same ? prev : r.data;
          });
        }
      });
    };
    fetchStats();
    const si = setInterval(fetchStats, 60000);
    return () => clearInterval(si);
  }, []);

  useEffect(() => {
    if (activeTab === "following") {
      setFollowingLoading(true);
      api("/posts/following").then(r => {
  if (r.ok) setFollowingPosts((r.data?.data || []).map(p => ({
    id:p.id, topic:p.topic?.slug||"general", authorId:p.user?.id,
    authorName:p.user?.name||"Unknown", authorEmoji:p.user?.avatar_emoji||"👤",
    authorVerified:p.user?.is_verified||false, authorTrust:p.user?.trust_score||0,
    authorPic:p.user?.avatar_url||null, authorPlan:p.user?.plan||"free",
    authorGender:p.user?.gender?(p.user.gender==="Male"?"M":p.user.gender==="Female"?"F":"B"):null,
    content:p.content, likes:p.like_count||0, comments:p.comment_count||0,
    reposts:p.repost_count||0, views:p.view_count||0,
    time:timeAgo(p.created_at), ts:new Date(p.created_at).getTime(),
    liked:p.is_liked||false, bookmarked:p.is_bookmarked||false,
    isFollowing: true,
    quotedPost:p.quoted_post||null, repostedPost:p.reposted_post||null,
  })));
  setFollowingLoading(false);
});
    }
    if (activeTab === "trending") {
      setTrendingLoading(true);
      api("/posts/trending").then(r => {
        if (r.ok) setTrendingPosts((r.data?.data || []).map(p => ({
          id:p.id, topic:p.topic?.slug||"general", authorId:p.user?.id,
          authorName:p.user?.name||"Unknown", authorEmoji:p.user?.avatar_emoji||"👤",
          authorVerified:p.user?.is_verified||false, authorTrust:p.user?.trust_score||0,
          authorPic:p.user?.avatar_url||null, authorPlan:p.user?.plan||"free",
          content:p.content, likes:p.like_count||0, comments:p.comment_count||0,
          time:timeAgo(p.created_at), ts:new Date(p.created_at).getTime(),
          liked:p.is_liked||false, bookmarked:p.is_bookmarked||false,
        })));
        setTrendingLoading(false);
      });
    }
    if (activeTab === "meetups") {
      api("/meetups").then(r => {
        if (r.ok) {
          setMeetupPreviews((r.data?.requests?.data || []).slice(0,5));
          const seen = parseInt(localStorage.getItem("gc_meetup_seen")||"0");
          setMeetupBadge(Math.max(0,(r.data?.requests?.total||0)-seen));
        }
      });
    }
    if (activeTab === "streaming") {
      api("/movies").then(r => {
        if (r.ok) setMoviePreviews((r.data?.movies||r.data?.data||(Array.isArray(r.data)?r.data:[])).slice(0,6));
      });
    }
    if (activeTab === "events") {
      api("/events").then(r => {
        if (r.ok) setEventPreviews((r.data?.data||r.data?.events||(Array.isArray(r.data)?r.data:[])).slice(0,5));
      });
    }
  }, [activeTab]);

  useEffect(() => {
    api("/meetups").then(r => {
      if (r.ok) {
        const seen = parseInt(localStorage.getItem("gc_meetup_seen")||"0");
        setMeetupBadge(Math.max(0,(r.data?.requests?.total||0)-seen));
      }
    });
  }, []);

  useEffect(() => { if (activeTab === "for_you") setTopic("all"); }, [activeTab]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "meetups") { localStorage.setItem("gc_meetup_seen", String(meetupPreviews.length)); setMeetupBadge(0); }
  };

  const forYouPosts = [...(filteredPosts||[])].sort((a,b) => {
    try {
      const score = (p) => {
        let s = 0;
        const ageHours = Math.max(0,(Date.now()-(p.ts||Date.now()))/3600000);
        s += Math.max(0,100-ageHours*2);
        s += (p.likes||0)*3; s += (p.comments||0)*5;
        if (p.authorPlan==="elite") s+=15;
        if (p.authorPlan==="premium") s+=8;
        if (p.authorVerified) s+=5;
        return s;
      };
      return score(b)-score(a);
    } catch(e) { return 0; }
  });

  const Spinner = () => (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"50px 0", gap:10 }}>
      <div style={{ width:32, height:32, border:`3px solid ${c.border}`, borderTopColor:"#6c5ce7", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ fontSize:12, color:c.faint }}>Loading...</div>
    </div>
  );


  const QuickIcons = () => (
    <div style={{ display:"flex", justifyContent:"space-around", padding:"8px 0 14px" }}>
      {[
        { icon:null, label:"Red Room", color:dark?"#3a0a0a":"#fff0f0", border:"#ef444430", page:"red_room", liveBtn:true },
        { icon:"🎮", label:"Games", color:dark?"#2a2000":"#fffbeb", border:"#f59e0b30", page:"games" },
        { icon:"🎟️", label:"Events", color:dark?"#002a1a":"#f0fdf4", border:"#10b98130", page:"events" },
        { icon:"🌍", label:"Global", color:dark?"#00182a":"#eff6ff", border:"#3b82f630", soon:true },
      ].map((item,i) => (
        <div key={i} onClick={() => item.page ? go(item.page) : flash("Coming soon! 🚀")} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer", position:"relative" }}>
          {item.liveBtn ? (
  <div style={{ position:"relative", width:54, height:54, display:"flex", alignItems:"center", justifyContent:"center" }}>
    {[0,0.6,1.2].map((delay,i) => (
      <div key={i} style={{ position:"absolute", borderRadius:"50%", width:44, height:44, border:"2px solid rgba(255,0,0,0.4)", animation:`gcPulse 2s ${delay}s infinite` }} />
    ))}
    <div style={{ width:36, height:36, borderRadius:"50%", background:"radial-gradient(circle at 30% 30%,#ff5a5a,#ff0000)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 0 3px rgba(255,0,0,0.08),0 0 16px rgba(255,0,0,0.6),0 0 28px rgba(255,0,0,0.4)", animation:"gcHeartbeat 1.1s infinite" }}>
      <div style={{ width:16, height:16, borderRadius:"50%", background:"#ff0000", boxShadow:"inset 0 2px 5px rgba(255,255,255,.35),0 0 8px rgba(255,0,0,0.9)" }} />
    </div>
    <div style={{ position:"absolute", top:-4, right:-8, background:"#ff0033", color:"white", fontSize:8, fontWeight:700, padding:"2px 5px", borderRadius:999, letterSpacing:".5px", boxShadow:"0 0 8px rgba(255,0,0,0.7)", animation:"gcBlink 1s infinite" }}>LIVE</div>
    <style>{`@keyframes gcPulse{0%{transform:scale(.7);opacity:.8;}100%{transform:scale(1.7);opacity:0;}}@keyframes gcHeartbeat{0%{transform:scale(1);}14%{transform:scale(1.12);}28%{transform:scale(1);}42%{transform:scale(1.12);}70%{transform:scale(1);}}@keyframes gcBlink{0%,50%,100%{opacity:1;}25%,75%{opacity:.35;}}`}</style>
  </div>
) : (
  <div style={{ width:54, height:54, borderRadius:27, background:item.color, border:`1.5px solid ${item.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>{item.icon}</div>
)}
          {item.soon && <div style={{ position:"absolute", top:-3, right:0, background:"linear-gradient(135deg,#6c5ce7,#a855f7)", color:"#fff", fontSize:7, fontWeight:800, padding:"2px 5px", borderRadius:5 }}>NEW</div>}
          <span style={{ fontSize:11, color:c.sub, fontWeight:600 }}>{item.label}</span>
        </div>
      ))}
    </div>
  );

  const OnlineStrip = () => {
    const onlineUsers = comStats.online_users?.slice(0, 5) || [];
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "8px 12px", background: dark ? "#ffffff06" : "#f8f8ff", borderRadius: 12, border: `1px solid ${c.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <style>{`@keyframes pulse2{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10b981", animation: "pulse2 2s infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}><span style={{ color: "#10b981" }}>{comStats.online_count}</span> online</span>
            <div style={{ display: "flex", marginLeft: 4 }}>
              {onlineUsers.map((u, i) => (
                <div key={u.id || i} style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${dark ? "#0b0c10" : "#fff"}`, marginLeft: i > 0 ? -8 : 0, background: u.avatar_url ? "transparent" : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", fontWeight: 700, overflow: "hidden", zIndex: 5 - i, position: "relative" }}>
                  {u.avatar_url
                    ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : (u.avatar_emoji || u.name?.[0] || "?")}
                </div>
              ))}
              {comStats.online_count > 5 && <div style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${dark ? "#0b0c10" : "#fff"}`, marginLeft: -8, background: "#6c5ce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 800, position: "relative", zIndex: 0 }}>+{comStats.online_count - 5}</div>}
            </div>
          </div>
          <button onClick={() => go("online_members")} style={{ fontSize: 11, color: "#6c5ce7", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>See all ›</button>
        </div>
      </>
    );
  };

  const FeedTabs = () => (
    <div style={{ display:"flex", gap:6, overflowX:"auto", scrollbarWidth:"none", marginBottom:14, paddingBottom:2 }}>
      {TABS.map(tab => {
        const isActive = activeTab===tab.id;
        return (
          <button key={tab.id} onClick={() => handleTabClick(tab.id)} style={{ padding:"7px 16px", borderRadius:20, whiteSpace:"nowrap", border:`1.5px solid ${isActive?"#6c5ce7":c.border}`, background:isActive?(dark?"#6c5ce720":"#f0edff"):(dark?c.card:c.bg), color:isActive?"#6c5ce7":c.sub, fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", gap:5, transition:"all 0.15s" }}>
            {tab.fire&&<FireIcon size={12}/>}
            {tab.label}
            {tab.badge&&meetupBadge>0&&<span style={{ minWidth:16, height:16, borderRadius:8, background:"#ef4444", color:"#fff", fontSize:9, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>{meetupBadge}</span>}
          </button>
        );
      })}
    </div>
  );

  const OnboardOverlay = () => {
    if (!showOnboard) return null;
    const steps = [
      { icon:"🔥", title:"Welcome to Geilstore Community!", desc:"Kenya's most trusted adult community. Real people, real connections.", color:"#6c5ce7" },
      { icon:"🛡️", title:"Get Verified, Get Trusted", desc:"Verified members get a blue tick, meetup access, and higher trust scores.", color:"#3b82f6" },
      { icon:"💬", title:"Post, Comment & Connect", desc:"Share thoughts, create polls, join trending topics.", color:"#10b981" },
      { icon:"🤖", title:"AI Matching", desc:"Let our AI find your best matches based on interests and location.", color:"#f59e0b" },
      { icon:"👑", title:"Go Premium or Elite", desc:"Unlock unlimited posts, movie streaming, and more. From KES 499/month.", color:"#7c6cff" },
    ];
    const step = steps[onboardStep];
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", backdropFilter:"blur(10px)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ width:"100%", maxWidth:380, background:c.card, borderRadius:24, overflow:"hidden", border:`1px solid ${c.border}` }}>
          <div style={{ background:`linear-gradient(135deg,${step.color},${step.color}88)`, padding:"36px 28px 28px", textAlign:"center" }}>
            <div style={{ width:72, height:72, borderRadius:36, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontSize:36 }}>{step.icon}</div>
            <h2 style={{ fontSize:20, fontWeight:800, color:"#fff", margin:"0 0 8px" }}>{step.title}</h2>
            <p style={{ fontSize:13, color:"rgba(255,255,255,0.85)", lineHeight:1.6, margin:0 }}>{step.desc}</p>
          </div>
          <div style={{ padding:"20px 28px 28px" }}>
            <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:20 }}>
              {steps.map((_,i) => <div key={i} style={{ width:i===onboardStep?24:8, height:8, borderRadius:4, background:i===onboardStep?step.color:c.border, transition:"all 0.3s" }} />)}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Btn full style={{ flex:1 }} onClick={() => setShowOnboard(false)}>Skip</Btn>
              <Btn primary full style={{ flex:2 }} onClick={() => { if(onboardStep<steps.length-1) setOnboardStep(onboardStep+1); else setShowOnboard(false); }}>{onboardStep<steps.length-1?"Next →":"Get Started 🚀"}</Btn>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MeetupsTab = () => (
    <div>
      <button onClick={() => go("meetups")} style={{ width:"100%", padding:"13px", borderRadius:14, border:"none", background:"linear-gradient(135deg,#6c5ce7,#e91e8c)", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", marginBottom:14 }}>💕 Post a Meetup Request</button>
      {meetupPreviews.length===0 ? <EmptyState icon="💕" title="No meetup requests yet" desc="Be the first to post!" /> :
        meetupPreviews.map(m => (
          <div key={m.id} onClick={() => go("meetups")} style={{ background:dark?c.card:"#fff", borderRadius:16, border:`1px solid ${c.border}`, padding:14, marginBottom:10, cursor:"pointer" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ width:42, height:42, borderRadius:21, background:m.user?.avatar_url?`url(${m.user.avatar_url}) center/cover`:"linear-gradient(135deg,#6c5ce7,#e91e8c)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, overflow:"hidden" }}>{!m.user?.avatar_url&&(m.user?.avatar_emoji||"👤")}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.text }}>{m.user?.name||"Anonymous"}</div>
                <div style={{ fontSize:11, color:c.faint }}>📍 {m.location} · {m.purpose}</div>
              </div>
              <div style={{ padding:"3px 8px", borderRadius:8, background:"#6c5ce720", color:"#6c5ce7", fontSize:10, fontWeight:700 }}>Age {m.age_min}-{m.age_max}</div>
            </div>
            <p style={{ fontSize:13, color:c.text, margin:"0 0 6px", lineHeight:1.5 }}>{m.description?.slice(0,100)}{m.description?.length>100?"...":""}</p>
            <div style={{ fontSize:11, color:c.faint }}>{m.interests_count||0} interested · Tap to view</div>
          </div>
        ))
      }
      <button onClick={() => go("meetups")} style={{ width:"100%", padding:"11px", borderRadius:12, border:`1px solid ${c.border}`, background:"transparent", color:"#6c5ce7", fontSize:13, fontWeight:700, cursor:"pointer", marginTop:4 }}>See All Meetup Requests →</button>
    </div>
  );

  const StreamingTab = () => (
    <div>
      {moviePreviews.length===0 ? <EmptyState icon="🎬" title="No movies yet" desc="Check back soon!" /> :
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
          {moviePreviews.map((m,i) => (
            <div key={m.id||i} onClick={() => go("movies")} style={{ background:dark?c.card:"#fff", borderRadius:14, border:`1px solid ${c.border}`, overflow:"hidden", cursor:"pointer" }}>
              <div style={{ height:100, background:m.thumbnail_url?`url(${m.thumbnail_url}) center/cover`:"linear-gradient(135deg,#ef4444,#dc2626)", display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
                {!m.thumbnail_url&&<span style={{ fontSize:32 }}>🎬</span>}
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ width:32, height:32, borderRadius:16, background:"rgba(255,255,255,0.9)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#6c5ce7"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </div>
                </div>
                {m.status==="live"&&<div style={{ position:"absolute", top:6, left:6, background:"#ef4444", color:"#fff", fontSize:9, fontWeight:800, padding:"2px 6px", borderRadius:6 }}>🔴 LIVE</div>}
              </div>
              <div style={{ padding:"8px 10px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:c.text, marginBottom:2 }}>{m.title||"Untitled"}</div>
                <div style={{ fontSize:10, color:c.faint }}>{m.category||"Exclusive"} · {m.view_count||0} views</div>
              </div>
            </div>
          ))}
        </div>
      }
      <button onClick={() => go("movies")} style={{ width:"100%", padding:"11px", borderRadius:12, border:`1px solid ${c.border}`, background:"transparent", color:"#ef4444", fontSize:13, fontWeight:700, cursor:"pointer" }}>See All Movies & Streams →</button>
    </div>
  );

  const EventsTab = () => (
    <div>
      {eventPreviews.length===0 ? <EmptyState icon="🎟️" title="No upcoming events" desc="Check back soon!" /> :
        eventPreviews.map((ev,i) => (
          <div key={ev.id||i} onClick={() => go("events")} style={{ background:dark?c.card:"#fff", borderRadius:16, border:`1px solid ${c.border}`, padding:14, marginBottom:10, cursor:"pointer", display:"flex", gap:12, alignItems:"center" }}>
            <div style={{ width:52, height:52, borderRadius:14, background:"linear-gradient(135deg,#f59e0b,#d97706)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <div style={{ fontSize:18, fontWeight:800, color:"#fff", lineHeight:1 }}>{ev.event_date?new Date(ev.event_date).getDate():"?"}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.85)", fontWeight:700 }}>{ev.event_date?new Date(ev.event_date).toLocaleString("default",{month:"short"}).toUpperCase():"TBA"}</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:c.text, marginBottom:3 }}>{ev.title||"Upcoming Event"}</div>
              <div style={{ fontSize:11, color:c.faint }}>📍 {ev.venue||ev.location||"TBA"} · {ev.price?`KES ${ev.price}`:"Free"}</div>
            </div>
            <span style={{ fontSize:16, color:c.faint }}>›</span>
          </div>
        ))
      }
      <button onClick={() => go("events")} style={{ width:"100%", padding:"11px", borderRadius:12, border:`1px solid ${c.border}`, background:"transparent", color:"#f59e0b", fontSize:13, fontWeight:700, cursor:"pointer" }}>See All Events →</button>
    </div>
  );

  // ── POST COMPOSER ──
  const Composer = () => {
  useEffect(() => {
    if (textRef.current) {
      textRef.current.removeAttribute("dir");
      textRef.current.setAttribute("dir", "ltr");
      textRef.current.style.cssText += ";direction:ltr!important;text-align:left!important;unicode-bidi:embed!important;writing-mode:horizontal-tb!important;";
      // Move cursor to end then focus — prevents reverse typing
      const len = textRef.current.value.length;
      textRef.current.focus();
      textRef.current.setSelectionRange(len, len);
    }
  }, []);
  return (
  <div style={{ position:"fixed", inset:0, zIndex:2000, display:"flex", flexDirection:"column", justifyContent:"flex-end", background:"rgba(0,0,0,0.6)", backdropFilter:"blur(6px)" }} onClick={resetComposer}>
  <div onClick={e=>e.stopPropagation()} dir="ltr" style={{ background:dark?"#1a1b2e":"#fff", borderRadius:"20px 20px 0 0", padding:"20px 18px 40px", maxHeight:"92vh", overflowY:"auto", boxShadow:"0 4px 24px rgba(0,0,0,0.07)" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <div style={{ width:40, height:40, borderRadius:20, background:user?.avatarUrl?`url(${user.avatarUrl}) center/cover`:c.grad, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, overflow:"hidden", flexShrink:0, border:`2px solid ${c.border}` }}>
          {!user?.avatarUrl&&(user?.emoji||"👤")}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.text }}>{user?.name||"You"}</div>
          <div style={{ fontSize:11, color:c.faint }}>Sharing with everyone</div>
        </div>
        <button onClick={resetComposer} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${c.border}`, background:"transparent", color:c.faint, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
      </div>

      {/* Text area */}<textarea
  ref={textRef}
  placeholder="Share something with the community..."
  value={newPost}
  onChange={e => {
  newPostRef.current = e.target.value.slice(0, 500);
  setNewPost(newPostRef.current);
}}
  autoComplete="off"
  rows={3}
  style={{
    width: "100%", padding: "8px 0",
    border: "none", background: "transparent", color: c.text,
    fontSize: 15, lineHeight: 1.6, outline: "none",
    fontFamily: "inherit", boxSizing: "border-box",
    resize: "none", overflow: "hidden"
  }}
/>

      {/* Media previews */}
      {mediaPreviews.length > 0 && (
        <div style={{ marginBottom:10, position:"relative" }}>
          {mediaType === "video" ? (
            <video src={mediaPreviews[0]?.url} controls style={{ width:"100%", maxHeight:240, borderRadius:12, display:"block", background:"#000" }} />
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:mediaPreviews.length===1?"1fr":"1fr 1fr", gap:6 }}>
              {mediaPreviews.map((p,i) => (
                <div key={i} style={{ position:"relative" }}>
                  <img src={p.url} alt="" style={{ width:"100%", height:"auto", maxHeight:400, objectFit:"contain", borderRadius:10, display:"block", background:"#000" }} />
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { setMediaFiles([]); setMediaPreviews([]); setMediaType(null); }} style={{ position:"absolute", top:6, right:6, width:26, height:26, borderRadius:13, border:"none", background:"rgba(0,0,0,0.6)", color:"#fff", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>
      )}

      {/* Sticker preview */}
      {postSticker && (
        <div style={{ position:"relative", marginBottom:10 }}>
          <img src={postSticker.url} alt={postSticker.name} style={{ width:"100%", maxWidth:200, maxHeight:200, objectFit:"contain", borderRadius:12, display:"block", border:`1px solid ${c.border}` }} />
          <button onClick={() => setPostSticker(null)} style={{ position:"absolute", top:6, right:6, width:26, height:26, borderRadius:13, border:"none", background:"rgba(0,0,0,0.6)", color:"#fff", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>
      )}

      {/* Poll creator */}
      {showPollCreator && (
        <div style={{ background:dark?"#ffffff08":"#f8f7ff", borderRadius:14, border:`1px solid ${c.border}`, padding:14, marginBottom:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#6c5ce7" }}>📊 Create Poll</span>
            <button onClick={() => { setShowPollCreator(false); setPollQuestion(""); setPollOptions(["",""]); }} style={{ background:"none", border:"none", color:c.faint, cursor:"pointer", fontSize:14 }}>✕</button>
          </div>
          <input style={{ width:"100%", padding:"9px 12px", borderRadius:9, border:`1.5px solid ${c.border}`, background:c.card, color:c.text, fontSize:13, outline:"none", marginBottom:8, boxSizing:"border-box", fontFamily:"inherit" }} placeholder="Ask a question..." value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} />
          {pollOptions.map((opt,i) => (
            <div key={i} style={{ display:"flex", gap:6, marginBottom:6 }}>
              <input style={{ flex:1, padding:"8px 12px", borderRadius:9, border:`1px solid ${c.border}`, background:c.card, color:c.text, fontSize:12, outline:"none", fontFamily:"inherit" }} placeholder={`Option ${i+1}`} value={opt} onChange={e => { const o=[...pollOptions]; o[i]=e.target.value; setPollOptions(o); }} />
              {pollOptions.length>2&&<button onClick={() => setPollOptions(pollOptions.filter((_,j)=>j!==i))} style={{ width:28, height:28, borderRadius:7, border:"none", background:"#ef444415", color:"#ef4444", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>}
            </div>
          ))}
          {pollOptions.length<6&&<button onClick={() => setPollOptions([...pollOptions,""])} style={{ width:"100%", padding:"7px", borderRadius:9, border:`1px dashed ${c.border}`, background:"transparent", color:"#6c5ce7", fontSize:12, fontWeight:600, cursor:"pointer" }}>+ Add option</button>}
        </div>
      )}

      {/* Sticker picker */}
      {showPostStickerPicker && (
        <div style={{ borderTop:`1px solid ${c.border}`, marginTop:8 }}>
          <StickerSheet darkMode={!!dark} onClose={() => setShowPostStickerPicker(false)} onSelect={s => { setPostSticker(s); setShowPostStickerPicker(false); }} />
        </div>
      )}

      {/* Divider */}
      <div style={{ borderTop:`1px solid ${c.border}`, marginTop:10, paddingTop:12 }}>
        {/* Action buttons row */}
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"nowrap", overflowX:"auto", scrollbarWidth:"none" }}>
          <div style={{ display:"flex", alignItems:"center", gap:2, flexShrink:0 }} ref={emojiRef}>
            {/* Emoji */}
            <button onClick={() => { setShowEmoji(!showEmoji); setEmojiTarget("post"); }} title="Emoji" style={{ width:36, height:36, borderRadius:8, border:"none", background:"transparent", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>😊</button>
            {/* Image */}
            <label title="Add Image" style={{ width:36, height:36, borderRadius:8, background:mediaPreviews.length&&mediaType==="image"?"#6c5ce720":"transparent", color:mediaPreviews.length&&mediaType==="image"?"#6c5ce7":c.sub, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
              <input type="file" accept="image/*" multiple style={{ display:"none" }} onChange={handleMediaSelect} />
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </label>
            {/* Video */}
            <label title="Add Video" style={{ width:36, height:36, borderRadius:8, background:mediaPreviews.length&&mediaType==="video"?"#e91e8c20":"transparent", color:mediaPreviews.length&&mediaType==="video"?"#e91e8c":c.sub, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
              <input type="file" accept="video/*" style={{ display:"none" }} onChange={handleMediaSelect} />
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </label>
            {/* Poll */}
            <button onClick={() => { setShowPollCreator(!showPollCreator); setMediaFiles([]); setMediaPreviews([]); }} title="Create Poll" style={{ width:36, height:36, borderRadius:8, border:"none", background:showPollCreator?"#6c5ce720":"transparent", color:showPollCreator?"#6c5ce7":c.sub, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>📊</button>
            
            <span style={{ fontSize:11, color:c.faint, marginLeft:4 }}>{newPostRef.current.length}/500</span>
            {showEmoji&&emojiTarget==="post"&&<EmojiPicker emojiRef={emojiRef} onSelect={em => { setNewPost(p=>p+em); textRef.current?.focus(); }} />}
          </div>
          <div style={{ display:"flex", gap:8, flexShrink:0, marginLeft:"auto" }}>
            <button onClick={resetComposer} style={{ padding:"8px 16px", borderRadius:10, border:`1px solid ${c.border}`, background:"transparent", color:c.sub, fontSize:13, fontWeight:600, cursor:"pointer" }}>Cancel</button>
            <button onClick={() => { doPost(postSticker?postSticker.id:null, mediaFiles, mediaType, newPostRef.current); setPostSticker(null); setShowPostStickerPicker(false); setMediaFiles([]); setMediaPreviews([]); setMediaType(null); }} disabled={loading||(!newPostRef.current.trim()&&!showPollCreator&&!postSticker&&!mediaFiles.length)} style={{ width:44, height:44, borderRadius:22, border:"none", background:"linear-gradient(135deg,#6c5ce7,#e91e8c)", cursor:"pointer", opacity:(loading||(!newPostRef.current.trim()&&!showPollCreator&&!postSticker&&!mediaFiles.length))?0.4:1, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {loading
                ? <div style={{ width:18, height:18, borderRadius:9, border:"2px solid #ffffff40", borderTopColor:"#fff", animation:"spin 0.8s linear infinite" }} />
                : <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M7 17L18 12L7 7L9 11L14 12L9 13L7 17Z" fill="#fff"/></svg>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      
    </div>
    </div>
  );
};

  return (
    <Shell title="Community" right={user?.isAdmin&&(
      <button onClick={() => go("admin")} style={{ width:34, height:34, borderRadius:9, border:`1px solid ${c.border}`, background:c.card, color:c.sub, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:15 }}>⚙️</button>
    )}>
      <OnboardOverlay />

      

      {/* INSTALL BANNER */}
      {showInstall&&(
        <div style={{ background:"linear-gradient(135deg,#6c5ce7,#a855f7)", borderRadius:16, padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:12, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="15" x2="12" y2="15" strokeWidth="4" strokeLinecap="round"/></svg>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#fff", marginBottom:2 }}>Get the Full Experience! 🚀</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", lineHeight:1.4 }}>Install the app — faster, smoother & works offline</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end" }}>
            <button onClick={handleInstall} style={{ padding:"7px 16px", borderRadius:8, background:"#fff", color:"#6c5ce7", border:"none", fontSize:12, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>Install Free</button>
            <button onClick={dismissInstall} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:10, cursor:"pointer", fontWeight:600 }}>Not now</button>
          </div>
        </div>
      )}

      {/* FOR YOU */}
      {activeTab==="for_you"&&(
        <div>
          <StoriesBar/>
          <QuickIcons/>
          <OnlineStrip/>
          <FeedTabs/>
          {showComposer && <Composer key="gc-composer"/>}
          {feedLoading?<Spinner/>:forYouPosts.length>0?forYouPosts.map(p=><PostCard key={p.id} post={p}/>):<EmptyState icon="💬" title="No posts yet" desc="Be the first to post something!"/>}
        </div>
      )}

      {/* OTHER TABS */}
      {activeTab!=="for_you"&&(
        <div>
          <FeedTabs/>
          {showComposer && <Composer key="gc-composer"/>}
          {activeTab==="following"&&(
            followingLoading?<Spinner/>:followingPosts.length===0?(
              <div style={{ textAlign:"center", padding:"50px 20px" }}>
                <div style={{ fontSize:44, marginBottom:10 }}>👥</div>
                <div style={{ fontSize:16, fontWeight:700, color:c.sub, marginBottom:8 }}>No posts from people you follow</div>
                <div style={{ fontSize:13, color:c.faint, marginBottom:20 }}>Follow more members to see their posts here</div>
                <Btn primary onClick={() => go("leaderboard")}>Discover People</Btn>
              </div>
            ):followingPosts.map(p=><PostCard key={p.id} post={p}/>)
          )}
          {activeTab==="trending"&&(
            trendingLoading?<Spinner/>:trendingPosts.length===0?<EmptyState icon="🔥" title="No trending posts yet"/>:trendingPosts.map(p=><PostCard key={p.id} post={p}/>)
          )}
          {activeTab==="meetups"&&<MeetupsTab/>}
          {activeTab==="streaming"&&<StreamingTab/>}
          {activeTab==="events"&&<EventsTab/>}
        </div>
      )}

    </Shell>
  );
};

export const OnlineMembersPage = () => {
  const { c, dark, go, setViewingUser } = useApp();
  const [stats, setStats] = useState({ online_count: 0, online_users: [], suggested: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("online");

  useEffect(() => {
    setLoading(true);
    api("/community/stats").then(r => {
      if (r.ok) setStats(r.data);
      setLoading(false);
    });
  }, []);

  const list = tab === "online" ? (stats.online_users || []) : (stats.suggested || []);
  const filtered = search.trim()
    ? list.filter(u => u.name.toLowerCase().includes(search.toLowerCase()))
    : list;
  const planOrder = { elite: 0, premium: 1, free: 2 };
  const sorted = [...filtered].sort((a, b) => (planOrder[a.plan] || 2) - (planOrder[b.plan] || 2));

  const UserAvatar = ({ u, size = 46 }) => (
    <div style={{ width: size, height: size, borderRadius: size / 2, flexShrink: 0, overflow: "hidden", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4, color: "#fff", fontWeight: 700 }}>
      {u.avatar_url
        ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : (u.avatar_emoji || u.name?.[0] || "?")}
    </div>
  );

  const PlanBadge = ({ plan }) => {
    if (plan === "elite") return <span style={{ fontSize: 9, fontWeight: 800, color: "#f59e0b", background: "#f59e0b15", padding: "1px 6px", borderRadius: 6 }}>ELITE</span>;
    if (plan === "premium") return <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", background: "#6c5ce715", padding: "1px 6px", borderRadius: 6 }}>PREMIUM</span>;
    return null;
  };

  return (
    <Shell title="Online Members" showBack onBack={() => go("feed")}>
      <div style={{ padding: "12px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: dark ? "#1a1b2e" : "#f4f4f8", borderRadius: 12, padding: "10px 14px", border: `1px solid ${c.border}`, marginBottom: 12 }}>
          <span style={{ fontSize: 14, color: c.faint }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..." style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13, color: c.text, fontFamily: "inherit" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", color: c.faint, cursor: "pointer", fontSize: 16 }}>✕</button>}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[{ id: "online", label: `🟢 Online (${stats.online_count})` }, { id: "suggested", label: "✨ Suggested" }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: `1.5px solid ${tab === t.id ? "#6c5ce7" : c.border}`, background: tab === t.id ? (dark ? "#6c5ce720" : "#f0edff") : "transparent", color: tab === t.id ? "#6c5ce7" : c.sub, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px 0", color: c.faint }}>Loading...</div>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 0", color: c.faint, fontSize: 13 }}>
            {search ? "No results found" : tab === "online" ? "Nobody online right now" : "No suggested members"}
          </div>
        ) : sorted.map(u => (
          <div key={u.id} onClick={() => { setViewingUser(u.id); go("user_profile"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${c.border}`, cursor: "pointer" }}>
            <div style={{ position: "relative" }}>
              <UserAvatar u={u} size={48} />
              {tab === "online" && <div style={{ position: "absolute", bottom: 1, right: 1, width: 12, height: 12, borderRadius: 6, background: "#10b981", border: `2px solid ${dark ? "#0b0c10" : "#fff"}` }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: c.text }}>{u.name}</span>
                {u.is_verified && <span style={{ fontSize: 12, color: "#3b82f6" }}>✓</span>}
                <PlanBadge plan={u.plan} />
              </div>
              <div style={{ fontSize: 11, color: c.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.bio || "Geilstore member"} · ★{u.trust_score || 0}
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); setViewingUser(u.id); go("user_profile"); }} style={{ padding: "7px 14px", borderRadius: 9, border: `1px solid ${c.border}`, background: "transparent", color: "#6c5ce7", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
              View
            </button>
          </div>
        ))}
      </div>
    </Shell>
  );
};

export default FeedPage;